import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { Box, Link, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { LEARNING_RESOLUTION_SCOPE } from "@/constants";
import { recordConceptChoice } from "@/features/chat/lib/chatConceptChoiceApi";
import type {
  LearningHistoryAlternative,
  LearningHistoryResolution,
} from "@/features/chat/model/chatTypes";

type LearningHistoryResolutionNoteProps = {
  resolutions: LearningHistoryResolution[];
  /** Hides every "Change" control, e.g. while the answer is still streaming. */
  canChange: boolean;
  /** The answer's server-assigned turn id; the change is recorded on it. No id, no "Change". */
  turnId?: string | null;
  /** Sends a question as a new chat message; without it the note is read-only. */
  onAskQuestion?: (question: string) => void;
};

/** Tells the user which terms were resolved from their Learning History, with a way to change them. */
export function LearningHistoryResolutionNote({
  resolutions,
  canChange,
  turnId = null,
  onAskQuestion,
}: LearningHistoryResolutionNoteProps) {
  if (resolutions.length === 0) {
    return null;
  }
  return (
    <Stack spacing={0.25} data-testid="learning-history-note" sx={{ mb: 1 }}>
      {resolutions.map((resolution) => (
        <ResolutionLine
          key={`${resolution.term}-${resolution.ownerType}-${resolution.ownerKey}`}
          resolution={resolution}
          turnId={turnId}
          onAskQuestion={canChange && turnId ? onAskQuestion : undefined}
        />
      ))}
    </Stack>
  );
}

function ResolutionLine({
  resolution,
  turnId,
  onAskQuestion,
}: {
  resolution: LearningHistoryResolution;
  turnId: string | null;
  onAskQuestion?: (question: string) => void;
}) {
  const { t } = useTranslation("chat");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canChange = Boolean(onAskQuestion) && resolution.alternatives.length > 0;

  const handleSelect = async (alternative: LearningHistoryAlternative) => {
    setAnchorEl(null);
    setIsSubmitting(true);
    try {
      if (turnId) {
        await recordConceptChoice(turnId, resolution, alternative);
      }
    } catch (error) {
      // The rewritten question still names the chosen meaning, so this answer is right either
      // way; only the chat's memory of the choice is lost.
      console.warn("[olive-ui] Could not record the concept choice", error);
    } finally {
      setIsSubmitting(false);
    }
    onAskQuestion?.(alternative.rewrittenQuestion);
  };

  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 0.5,
        color: "var(--theme-text-secondary)",
        "& strong": { color: "var(--theme-text-primary)", fontWeight: 600 },
      }}
    >
      <HistoryOutlinedIcon sx={{ fontSize: 14 }} />
      <Box component="span">
        <Trans
          t={t}
          i18nKey="learningHistoryNote.using"
          values={{ label: resolution.label, term: resolution.term }}
          components={{ strong: <strong /> }}
        />
      </Box>
      <Box component="span" aria-hidden>
        ·
      </Box>
      <Box component="span">
        {resolution.scope === LEARNING_RESOLUTION_SCOPE.DOMAIN
          ? t("learningHistoryNote.domainSource")
          : t("learningHistoryNote.source")}
      </Box>
      {canChange ? (
        <>
          <Box component="span" aria-hidden>
            ·
          </Box>
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            disabled={isSubmitting}
            aria-haspopup="true"
            aria-expanded={anchorEl !== null}
            aria-label={t("learningHistoryNote.changeAria", { term: resolution.term })}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{
              color: "var(--theme-primary)",
              fontWeight: 600,
              verticalAlign: "baseline",
              "&:disabled": { color: "var(--theme-text-secondary)", cursor: "default" },
            }}
          >
            {t("learningHistoryNote.change")}
          </Link>
          <Menu
            anchorEl={anchorEl}
            open={anchorEl !== null}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  minWidth: 180,
                  bgcolor: "var(--theme-surface)",
                  border: "1px solid var(--theme-border)",
                  borderRadius: 1.5,
                },
              },
            }}
          >
            {resolution.alternatives.map((alternative) => (
              <MenuItem
                key={`${alternative.ownerType}-${alternative.ownerKey}`}
                onClick={() => void handleSelect(alternative)}
                sx={{ color: "var(--theme-text-primary)", typography: "body2" }}
              >
                {alternative.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}
    </Typography>
  );
}
